'use client';

import {Button as ButtonPrimitive} from '@base-ui/react/button';
import {cva, type VariantProps} from 'class-variance-authority';

import {Spinner} from './spinner';
import {cn} from '../../lib/utils';

import type {ReactNode} from 'react';

const buttonVariantsConfig = {
  variants: {
    variant: {
      primary: [
        'bg-brand-primary text-fg_on-brand shadow-primary ring-1 ring-transparent ring-inset',
        'before:absolute before:inset-px before:rounded-[inherit] before:border before:border-white/12 before:pointer-events-none',
        'hover:bg-brand-primary_hover',
        'disabled:bg-background-disabled disabled:text-fg-disabled disabled:shadow-xs disabled:ring-border-disabled disabled:before:hidden'
      ].join(' '),
      secondary: [
        'bg-background-surface text-fg-secondary shadow-xs-skeumorphic ring-1 ring-border ring-inset',
        'hover:bg-background-surface_hover hover:text-fg-secondary_hover',
        'aria-expanded:bg-background-surface_hover aria-expanded:text-fg',
        'disabled:bg-background-disabled disabled:text-fg-disabled disabled:shadow-xs disabled:ring-border-disabled'
      ].join(' '),
      tertiary: [
        'text-fg-tertiary',
        'hover:bg-background-surface_hover hover:text-fg',
        'aria-expanded:bg-background-surface_hover aria-expanded:text-fg',
        'disabled:text-fg-disabled'
      ].join(' '),
      selected: [
        'bg-background-selected text-brand-accent',
        'hover:bg-background-selected-strong',
        'aria-expanded:bg-background-selected-strong',
        'disabled:bg-background-disabled disabled:text-fg-disabled'
      ].join(' '),
      linkColor: 'text-brand-accent underline-offset-4 hover:underline disabled:text-fg-disabled',
      linkGray:
        'text-fg-tertiary underline-offset-4 hover:underline hover:text-fg disabled:text-fg-disabled'
    },
    destructive: {
      true: ''
    },
    elevated: {
      true: ''
    },
    size: {
      default:
        'h-10 gap-1 px-3.5 py-2.5 in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pe-2.5 has-data-[icon=inline-start]:ps-2.5',
      xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pe-1.5 has-data-[icon=inline-start]:ps-1.5 [&_svg:not([class*='size-'])]:size-3",
      sm: 'h-9 gap-1 rounded-[min(var(--radius-md),10px)] px-3 py-2 in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2',
      lg: 'h-11 gap-1.5 px-4 py-3 has-data-[icon=inline-end]:pe-3 has-data-[icon=inline-start]:ps-3',
      xl: 'h-12 gap-1.5 px-[18px] py-3 text-base has-data-[icon=inline-end]:pe-3.5 has-data-[icon=inline-start]:ps-3.5',
      stretch: 'h-10 gap-1 px-3.5 py-2.5 flex-1 w-full',
      icon: 'size-10 rounded-[min(var(--radius-md),8px)]',
      'icon-xs':
        "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
      'icon-sm':
        'size-9 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg',
      'icon-lg': 'size-11',
      'icon-xl': 'size-12'
    }
  },
  compoundVariants: [
    {
      variant: 'primary',
      destructive: true,
      class: 'bg-destructive hover:bg-destructive_hover'
    },
    {
      variant: 'secondary',
      destructive: true,
      class: [
        'ring-border-error_subtle text-destructive',
        'hover:bg-background-error hover:text-destructive_hover',
        'aria-expanded:bg-background-error aria-expanded:text-destructive'
      ].join(' ')
    },
    {
      variant: 'tertiary',
      destructive: true,
      class: [
        'text-destructive',
        'hover:bg-background-error hover:text-destructive_hover',
        'aria-expanded:bg-background-error aria-expanded:text-destructive'
      ].join(' ')
    },
    {
      variant: 'linkColor',
      destructive: true,
      class: 'text-destructive hover:text-destructive_hover'
    },
    {
      variant: 'primary',
      elevated: true,
      class: 'shadow-fab'
    }
  ],
  defaultVariants: {
    variant: 'primary',
    size: 'default'
  }
} as const;

const buttonVariants = cva(
  [
    'relative overflow-clip rounded-lg text-sm font-semibold cursor-pointer',
    "inline-flex items-center justify-center whitespace-nowrap transition-all [&_svg:not([class*='size-'])]:size-5 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0",
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
    'aria-invalid:ring-destructive/20 aria-invalid:ring-3',
    'disabled:pointer-events-none disabled:cursor-not-allowed aria-busy:pointer-events-none aria-busy:cursor-progress',
    'group/button select-none'
  ].join(' '),
  {
    ...buttonVariantsConfig,
    compoundVariants: [...buttonVariantsConfig.compoundVariants]
  }
);

/** `isLoading` swaps the icon for a spinner, marks the button busy and blocks clicks while keeping keyboard focus. */
function Button({
  className,
  variant = 'primary',
  size = 'default',
  destructive,
  elevated,
  icon,
  iconPosition = 'start',
  isLoading = false,
  disabled,
  focusableWhenDisabled,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    icon?: ReactNode;
    iconPosition?: 'start' | 'end';
    isLoading?: boolean;
  }) {
  const hasChildren = children != null;
  const shownIcon = isLoading ? <Spinner size={null} /> : icon;
  const iconNode =
    shownIcon == null ? null : hasChildren ? (
      <span data-icon={iconPosition === 'start' ? 'inline-start' : 'inline-end'} data-slot="icon">
        {shownIcon}
      </span>
    ) : (
      shownIcon
    );

  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({variant, size, destructive, elevated, className}))}
      disabled={disabled || isLoading}
      focusableWhenDisabled={focusableWhenDisabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {iconPosition !== 'end' && iconNode}
      {children}
      {iconPosition === 'end' && iconNode}
    </ButtonPrimitive>
  );
}

export {Button, buttonVariants, buttonVariantsConfig};
